import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, ChevronRightLine, WarningLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {
  type EmployeeRoleLevel,
  type WorkstreamAuditCompanyGroup,
  type WorkstreamAuditReadiness,
  type WorkstreamAuditRow,
} from 'client';
import {EmptyState} from 'components/DSYS/EmptyState';
import {Link} from 'components/DSYS/Link';
import type {Column} from 'components/DSYS/Table';
import {Table} from 'components/DSYS/Table';
import {OwnerLink} from 'components/workstreams/OwnerLink';
import {t} from 'i18next';
import {Fragment, useState} from 'react';
import {useLocation} from 'react-router';
import {buildSidebarOriginState} from 'views/workstreams/routeUtils';
import {getWorkstreamHealthScoreUrl} from 'views/workstreams/utils';
import {HealthScoreChecks, HealthScoreIcon} from 'views/workstreams/WorkstreamHealthScore';

import {WorkstreamAuditOnTimeUpdatesGauge} from './WorkstreamAuditOnTimeUpdatesGauge';
import styles from './WorkstreamAuditTable.module.css';
import {
  WORKSTREAM_AUDIT_REPORTER_TAB,
  WORKSTREAM_AUDIT_STEAM_OWNER_TAB,
  type WorkstreamAuditTab,
} from './WorkstreamAuditTabs';

export const WORKSTREAM_AUDIT_TABLE_TEST_ID = 'workstream-audit-table';
export const WORKSTREAM_AUDIT_EMPTY_TEST_ID = 'workstream-audit-empty';
export const getWorkstreamAuditCompanyParentTestId = (id: number) =>
  `workstream-audit-company-parent-${id}`;
export const getWorkstreamAuditCompanyChildTestId = (id: number) =>
  `workstream-audit-company-child-${id}`;
export const WORKSTREAM_AUDIT_DRI_OWNERSHIP_WARNING_TEST_ID =
  'workstream-audit-dri-ownership-warning';

type WorkstreamAuditSecondaryOwnerColumn = 'exec_sponsor' | 'reporter' | 'steam_owner';

const getWorkstreamAuditSecondaryOwnerColumn = (
  auditTab: WorkstreamAuditTab
): WorkstreamAuditSecondaryOwnerColumn =>
  auditTab === WORKSTREAM_AUDIT_REPORTER_TAB
    ? 'reporter'
    : auditTab === WORKSTREAM_AUDIT_STEAM_OWNER_TAB
      ? 'steam_owner'
      : 'exec_sponsor';

const getWorkstreamAuditColumns = (
  secondaryOwnerColumn: WorkstreamAuditSecondaryOwnerColumn
): Column<WorkstreamAuditRow>[] => [
  {type: 'name', minWidth: 280, spanner: true},
  {type: 'dri', minWidth: 160, width: 210, maxWidth: 260},
  {type: secondaryOwnerColumn, minWidth: 200, width: 210, maxWidth: 260},
  {
    type: 'readiness',
    minWidth: 300,
    maxWidth: 450,
    tooltip: t('health_readiness_definition_tooltip'),
  },
  {type: 'on_time_updates', minWidth: 135, width: 135, maxWidth: 135},
];

export const getOverloadedWorkstreamAuditDriIds = (
  groups: WorkstreamAuditCompanyGroup[]
): Set<string> => {
  const workstreamIdsByDriId = new Map<string, Set<number>>();

  groups.forEach((group) => {
    [group.parent, ...(group.subworkstreams ?? [])].filter(Boolean).forEach((row) => {
      const driId = row.dri?.user_id?.trim();
      if (!driId || !Number.isInteger(row.id) || row.id <= 0) return;

      const workstreamIds = workstreamIdsByDriId.get(driId) ?? new Set<number>();
      workstreamIds.add(row.id);
      workstreamIdsByDriId.set(driId, workstreamIds);
    });
  });

  return new Set(
    [...workstreamIdsByDriId.entries()]
      .filter(([, workstreamIds]) => workstreamIds.size > 1)
      .map(([driId]) => driId)
  );
};

type WorkstreamAuditRowType = 'role' | 'company_parent' | 'company_child';

const WorkstreamAuditNameCell = ({
  row,
  auditTab,
  rowType,
  isCompanyParent = false,
  isCompanyChild = false,
}: {
  row: WorkstreamAuditRow;
  auditTab: WorkstreamAuditTab;
  rowType: WorkstreamAuditRowType;
  isCompanyParent?: boolean;
  isCompanyChild?: boolean;
}) => {
  const location = useLocation();
  const name = typeof row.name === 'string' ? row.name.trim() : '';
  const hasDetailRoute = Number.isInteger(row.id) && row.id > 0;
  const sidebarOriginState = buildSidebarOriginState({
    search: location.search,
    state: location.state,
  });

  const nameContent = (
    <Text
      className={styles.nameText}
      color={name ? 'standard' : 'subtle'}
      isBold={Boolean(name)}
      size={isCompanyParent ? 'small' : undefined}
    >
      {!name ? (
        t('workstream_audit_missing_value')
      ) : hasDetailRoute ? (
        <Link
          to={getWorkstreamHealthScoreUrl(row.id)}
          state={{source: 'table_row', ...sidebarOriginState}}
          hasNoUnderline
          showUnderlineOnHover
          monochromatic
          className={styles.nameLink}
          onClick={() =>
            analyticsLogger().logEvent('WORKSTREAM_AUDIT_ROW_CLICKED', {
              workstream_id: row.id,
              tab: auditTab,
              row_type: rowType,
            })
          }
        >
          <Truncate lines={2} tooltipControlProps={{placement: 'bottom'}}>
            {name}
          </Truncate>
        </Link>
      ) : (
        <Truncate lines={2} tooltipControlProps={{placement: 'bottom'}}>
          {name}
        </Truncate>
      )}
    </Text>
  );

  return (
    <Table.Cell className={styles.nameCell}>
      {isCompanyParent ? (
        <Box display="flex" alignItems="center" className={styles.companyParentName}>
          {nameContent}
        </Box>
      ) : isCompanyChild ? (
        <Box paddingLeft="24" className={styles.companyChildName}>
          {nameContent}
        </Box>
      ) : (
        nameContent
      )}
    </Table.Cell>
  );
};

const WorkstreamAuditOwnerCell = ({
  owner,
  showDriOwnershipWarning = false,
}: {
  owner?: EmployeeRoleLevel | null;
  showDriOwnershipWarning?: boolean;
}) => {
  const warningLabel = t('workstream_audit_dri_ownership_warning').toString();

  return (
    <Table.Cell>
      {owner ? (
        <div className={styles.ownerCellContent}>
          <OwnerLink owner={owner} />
          {showDriOwnershipWarning ? (
            <Tooltip title={warningLabel}>
              <span
                className={styles.driOwnershipWarning}
                data-testid={WORKSTREAM_AUDIT_DRI_OWNERSHIP_WARNING_TEST_ID}
                role="img"
                aria-label={warningLabel}
                tabIndex={0}
              >
                <UIIcon
                  src={WarningLine}
                  size="small"
                  className={atoms({color: 'Alert Base'})}
                  aria-hidden="true"
                />
              </span>
            </Tooltip>
          ) : null}
        </div>
      ) : (
        <Text color="subtle">{t('workstream_audit_missing_value')}</Text>
      )}
    </Table.Cell>
  );
};

const WorkstreamAuditReadinessCell = ({
  workstreamId,
  readiness,
  showDetailsDisclosure = false,
}: {
  workstreamId: number;
  readiness?: WorkstreamAuditReadiness;
  showDetailsDisclosure?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const score = readiness?.score;
  const failedCheckIds = readiness?.failed_check_ids ?? [];
  const failedChecks = failedCheckIds.map((id) => ({id, passed: false}));
  const hasDisclosure = Boolean(score && showDetailsDisclosure && failedChecks.length > 0);
  const detailsId = `workstream-audit-readiness-details-${workstreamId}`;

  return (
    <Table.Cell>
      <Stack gap="8">
        {score && hasDisclosure ? (
          <button
            type="button"
            className={styles.readinessDisclosure}
            aria-expanded={isExpanded}
            aria-controls={detailsId}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            <Box className={styles.readinessDisclosureIcon}>
              <UIIcon
                src={isExpanded ? ChevronDownLine : ChevronRightLine}
                size="small"
                className={atoms({color: 'Text Subtle'})}
                aria-hidden="true"
              />
            </Box>
            <HealthScoreIcon score={score} iconSize={24} textSize="small" showText isBold={false} />
          </button>
        ) : score ? (
          <HealthScoreIcon score={score} iconSize={24} textSize="small" showText isBold={false} />
        ) : (
          <Text color="subtle">{t('workstream_audit_missing_value')}</Text>
        )}
        {failedChecks.length > 0 && (!showDetailsDisclosure || isExpanded) ? (
          <Stack
            gap="8"
            id={hasDisclosure ? detailsId : undefined}
            className={hasDisclosure ? styles.readinessDisclosureDetails : undefined}
          >
            <HealthScoreChecks entityId={workstreamId} checks={failedChecks} />
          </Stack>
        ) : null}
      </Stack>
    </Table.Cell>
  );
};

const WorkstreamAuditTableRow = ({
  row,
  auditTab,
  overloadedDriIds,
  secondaryOwnerColumn,
  isCompanyChild = false,
}: {
  row: WorkstreamAuditRow;
  auditTab: WorkstreamAuditTab;
  overloadedDriIds: Set<string>;
  secondaryOwnerColumn: WorkstreamAuditSecondaryOwnerColumn;
  isCompanyChild?: boolean;
}) => (
  <Table.Row
    data-testid={isCompanyChild ? getWorkstreamAuditCompanyChildTestId(row.id) : undefined}
  >
    <WorkstreamAuditNameCell
      row={row}
      auditTab={auditTab}
      rowType={isCompanyChild ? 'company_child' : 'role'}
      isCompanyChild={isCompanyChild}
    />
    <WorkstreamAuditOwnerCell
      owner={row.dri}
      showDriOwnershipWarning={overloadedDriIds.has(row.dri?.user_id?.trim() ?? '')}
    />
    <WorkstreamAuditOwnerCell owner={row[secondaryOwnerColumn]} />
    <WorkstreamAuditReadinessCell
      workstreamId={row.id}
      readiness={row.readiness}
      showDetailsDisclosure={isCompanyChild}
    />
    <Table.Cell className={styles.onTimeUpdatesCell}>
      <WorkstreamAuditOnTimeUpdatesGauge onTimeUpdates={row.on_time_updates} />
    </Table.Cell>
  </Table.Row>
);

const WorkstreamAuditCompanyParentRow = ({
  group,
  auditTab,
  overloadedDriIds,
  secondaryOwnerColumn,
}: {
  group: WorkstreamAuditCompanyGroup;
  auditTab: WorkstreamAuditTab;
  overloadedDriIds: Set<string>;
  secondaryOwnerColumn: WorkstreamAuditSecondaryOwnerColumn;
}) => {
  return (
    <Table.Row
      className={styles.companyParentRow}
      data-testid={getWorkstreamAuditCompanyParentTestId(group.parent.id)}
    >
      <WorkstreamAuditNameCell
        row={group.parent}
        auditTab={auditTab}
        rowType="company_parent"
        isCompanyParent
      />
      <WorkstreamAuditOwnerCell
        owner={group.parent.dri}
        showDriOwnershipWarning={overloadedDriIds.has(group.parent.dri?.user_id?.trim() ?? '')}
      />
      <WorkstreamAuditOwnerCell owner={group.parent[secondaryOwnerColumn]} />
      <WorkstreamAuditReadinessCell
        workstreamId={group.parent.id}
        readiness={group.parent.readiness}
        showDetailsDisclosure
      />
      <Table.Cell className={styles.onTimeUpdatesCell}>
        <WorkstreamAuditOnTimeUpdatesGauge onTimeUpdates={group.parent.on_time_updates} />
      </Table.Cell>
    </Table.Row>
  );
};

export const WorkstreamAuditTable = ({
  rows,
  companyGroups,
  driOwnershipCompanyGroups,
  isCompanyMode = false,
  auditTab,
  showEmptyState = true,
}: {
  rows?: WorkstreamAuditRow[];
  companyGroups?: WorkstreamAuditCompanyGroup[];
  driOwnershipCompanyGroups?: WorkstreamAuditCompanyGroup[];
  isCompanyMode?: boolean;
  auditTab: WorkstreamAuditTab;
  showEmptyState?: boolean;
}) => {
  const safeRows = (rows ?? []).filter((row): row is WorkstreamAuditRow => Boolean(row));
  const safeCompanyGroups = (companyGroups ?? []).filter(
    (group): group is WorkstreamAuditCompanyGroup => Boolean(group?.parent)
  );
  const safeDriOwnershipCompanyGroups = (driOwnershipCompanyGroups ?? safeCompanyGroups).filter(
    (group): group is WorkstreamAuditCompanyGroup => Boolean(group?.parent)
  );
  const overloadedDriIds = getOverloadedWorkstreamAuditDriIds(safeDriOwnershipCompanyGroups);
  const secondaryOwnerColumn = getWorkstreamAuditSecondaryOwnerColumn(auditTab);
  const columns = getWorkstreamAuditColumns(secondaryOwnerColumn);
  const secondaryOwnerColumnLabel =
    secondaryOwnerColumn === 'reporter'
      ? 'workstream_role_label_reporter'
      : secondaryOwnerColumn === 'steam_owner'
        ? 'workstream_role_label_steam_owner'
        : 'workstream_audit_exec_sponsor_tab';

  const tableRows = isCompanyMode
    ? safeCompanyGroups.flatMap((group) => [
        group.parent,
        ...(group.subworkstreams ?? []).filter((row): row is WorkstreamAuditRow => Boolean(row)),
      ])
    : safeRows;

  return (
    <div className={styles.table} data-testid={WORKSTREAM_AUDIT_TABLE_TEST_ID}>
      <Table
        columns={columns}
        data={tableRows}
        border
        resizable={false}
        headerTextSize="small"
        columnLabelOverrides={{
          name: 'name',
          dri: 'dri',
          [secondaryOwnerColumn]: secondaryOwnerColumnLabel,
          readiness: 'health_readiness',
          on_time_updates: 'workstream_audit_on_time_updates',
        }}
      >
        <Table.Header />
        <Table.Body>
          {isCompanyMode && safeCompanyGroups.length > 0 ? (
            safeCompanyGroups.map((group) => {
              const children = (group.subworkstreams ?? []).filter(
                (row): row is WorkstreamAuditRow => Boolean(row)
              );

              return (
                <Fragment key={group.parent.id}>
                  <WorkstreamAuditCompanyParentRow
                    group={group}
                    auditTab={auditTab}
                    overloadedDriIds={overloadedDriIds}
                    secondaryOwnerColumn={secondaryOwnerColumn}
                  />
                  {children.map((row) => (
                    <WorkstreamAuditTableRow
                      key={row.id}
                      row={row}
                      auditTab={auditTab}
                      overloadedDriIds={overloadedDriIds}
                      secondaryOwnerColumn={secondaryOwnerColumn}
                      isCompanyChild
                    />
                  ))}
                </Fragment>
              );
            })
          ) : safeRows.length > 0 ? (
            <Table.Rows
              renderRow={(row: WorkstreamAuditRow) => (
                <WorkstreamAuditTableRow
                  row={row}
                  auditTab={auditTab}
                  overloadedDriIds={overloadedDriIds}
                  secondaryOwnerColumn={secondaryOwnerColumn}
                />
              )}
            />
          ) : showEmptyState ? (
            <Table.Row>
              <Table.Cell colSpan={columns.length} data-testid={WORKSTREAM_AUDIT_EMPTY_TEST_ID}>
                <EmptyState
                  hideBorder
                  image={null}
                  marginBottom="0"
                  paddingY="44"
                  title={t('workstream_audit_empty_title')}
                  body={t('workstream_audit_empty_body')}
                />
              </Table.Cell>
            </Table.Row>
          ) : null}
        </Table.Body>
      </Table>
    </div>
  );
};
